

import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddTaxModal = ({ isOpen, toggle, onSubmit }) => {
  const validation = useFormik({
    initialValues: {
      name: "",
      taxType: "",
      igst: "",
      cgst: "",
      sgst: "",
      taxPerc: "",
    },

    validationSchema: Yup.lazy((values) =>
      Yup.object({
        name: Yup.string().required("Tax name is required"),
        taxType: Yup.string().required("Tax type is required"),

        // IGST mode
        igst:
          values.taxType === "IGST"
            ? Yup.number()
                .typeError("IGST must be a number")
                .required("IGST is required")
                .min(0)
                .max(100)
            : Yup.number().nullable(),

        // CGST / SGST mode
        cgst:
          values.taxType === "C&SGST"
            ? Yup.number()
                .typeError("CGST must be a number")
                .required("CGST is required")
                .min(0)
                .max(100)
            : Yup.number().nullable(),

        sgst:
          values.taxType === "C&SGST"
            ? Yup.number()
                .typeError("SGST must be a number")
                .required("SGST is required")
                .min(0)
                .max(100)
            : Yup.number().nullable(),

        taxPerc: Yup.number()
          .typeError("Tax percentage must be a number")
          .required("Total tax percentage is required"),
      })
    ),

    onSubmit: (values) => {
      const payload = {
        name: values.name,
        taxType: values.taxType,
        taxPerc: values.taxPerc,
        igst: values.taxType === "IGST" ? values.igst : null,
        cgst: values.taxType === "C&SGST" ? values.cgst : null,
        sgst: values.taxType === "C&SGST" ? values.sgst : null,
      };

      onSubmit(payload);
      toggle();
      validation.resetForm();
    },
  });

  // Auto-calc logic
  useEffect(() => {
    const { taxType, igst, cgst } = validation.values;

    if (taxType === "IGST") {
      validation.setFieldValue("taxPerc", igst || "");
    }

    if (taxType === "C&SGST") {
      validation.setFieldValue("sgst", cgst || "");
      const total = cgst ? Number(cgst) * 2 : "";
      validation.setFieldValue("taxPerc", total);
    }
  }, [
    validation.values.taxType,
    validation.values.igst,
    validation.values.cgst,
  ]);

  useEffect(() => {
    if (!isOpen) validation.resetForm();
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered fade>
      <ModalHeader toggle={toggle} className="bg-light">
        Add Tax
      </ModalHeader>

      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          {/* Tax Name */}
          <FormGroup>
            <Label>
              Tax Name <span className="text-danger">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter tax name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={validation.touched.name && !!validation.errors.name}
            />
            <FormFeedback>{validation.errors.name}</FormFeedback>
          </FormGroup>

          {/* Tax Type */}
          <FormGroup>
            <Label>
              Tax Type <span className="text-danger">*</span>
            </Label>
            <Input
              id="taxType"
              name="taxType"
              type="select"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.taxType}
              invalid={
                validation.touched.taxType && !!validation.errors.taxType
              }
            >
              <option value="">Select Tax Type</option>
              <option value="IGST">IGST</option>
              <option value="C&SGST">CGST & SGST</option>
            </Input>
            <FormFeedback>{validation.errors.taxType}</FormFeedback>
          </FormGroup>

          {/* IGST Field */}
          {validation.values.taxType === "IGST" && (
            <FormGroup>
              <Label>
                IGST (%) <span className="text-danger">*</span>
              </Label>
              <Input
                id="igst"
                name="igst"
                type="number"
                placeholder="Enter IGST percentage"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.igst}
                invalid={validation.touched.igst && !!validation.errors.igst}
              />
              <FormFeedback>{validation.errors.igst}</FormFeedback>
            </FormGroup>
          )}

          {/* CGST / SGST Fields */}
          {validation.values.taxType === "C&SGST" && (
            <>
              <FormGroup>
                <Label>
                  CGST (%) <span className="text-danger">*</span>
                </Label>
                <Input
                  id="cgst"
                  name="cgst"
                  type="number"
                  placeholder="Enter CGST percentage"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.cgst}
                  invalid={validation.touched.cgst && !!validation.errors.cgst}
                />
                <FormFeedback>{validation.errors.cgst}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label>
                  SGST (%) <span className="text-danger">*</span>
                </Label>
                <Input
                  id="sgst"
                  name="sgst"
                  type="number"
                  value={validation.values.sgst}
                  invalid={validation.touched.sgst && !!validation.errors.sgst}
                />
                <FormFeedback>{validation.errors.sgst}</FormFeedback>
              </FormGroup>
            </>
          )}

          {/* Total Tax */}
          <FormGroup>
            <Label>
              Total Tax (%) <span className="text-danger">*</span>
            </Label>
            <Input
              id="taxPerc"
              name="taxPerc"
              type="number"
              value={validation.values.taxPerc}
              readOnly
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" className="btn-sm" onClick={toggle}>
            Cancel
          </Button>
          <Button color="success" className="btn-sm" type="submit">
            Add Tax
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddTaxModal;
